// Story E6.3.2 — PresenceDot: bolinha colorida sobreposta em Avatar.
//
// Uso (overlay):
//   <div className="relative">
//     <Avatar>...</Avatar>
//     <PresenceDot userId={user.id} className="absolute bottom-0 right-0" />
//   </div>
//
// Uso (inline, em lista textual):
//   <PresenceDot userId={user.id} size="sm" />
//
// O Dot consulta o PresenceContext — só renderiza dentro do Provider.
// Fora dele, retorna null (degradação graceful para login/error screens).

import { cn } from "@/lib/utils";
import { usePresenceSafe } from "@/contexts/PresenceContext";
import type { PresenceStatus } from "@/services/presenceService";

interface Props {
  userId: string;
  size?: "xs" | "sm" | "md";
  className?: string;
  /** Mostra dot mesmo quando offline (cinza). Default true. */
  showOffline?: boolean;
  /** Override de status (modo controlado — usado nos testes). */
  statusOverride?: PresenceStatus;
}

// Mapping spec literal:
// - online: verde-500
// - away:   amarelo-500
// - busy:   vermelho-500
// - offline: cinza-400 (low contrast, sinaliza "ausente sem alarme")
const STATUS_COLOR: Record<PresenceStatus, string> = {
  online: "bg-green-500",
  away: "bg-yellow-500",
  busy: "bg-red-500",
  offline: "bg-gray-400",
};

const STATUS_LABEL: Record<PresenceStatus, string> = {
  online: "Online",
  away: "Ausente",
  busy: "Ocupado",
  offline: "Offline",
};

const SIZE_CLASS: Record<NonNullable<Props["size"]>, string> = {
  xs: "h-2 w-2 border",
  sm: "h-2.5 w-2.5 border-2",
  md: "h-3 w-3 border-2",
};

export function PresenceDot({
  userId,
  size = "sm",
  className,
  showOffline = true,
  statusOverride,
}: Props) {
  const { lookupStatus } = usePresenceSafe();
  const status = statusOverride ?? lookupStatus(userId);

  if (status === "offline" && !showOffline) return null;

  return (
    <span
      role="status"
      aria-label={`Presença: ${STATUS_LABEL[status]}`}
      data-testid={`presence-dot-${userId}`}
      data-status={status}
      title={STATUS_LABEL[status]}
      className={cn(
        "inline-block rounded-full border-background",
        SIZE_CLASS[size],
        STATUS_COLOR[status],
        className
      )}
    />
  );
}
